import { useState } from "react";
import { renderLinkifiedTextYouTubeStrict } from "@/utils/linkify";

interface MediaPreviewCardProps {
  audioSrc?: string;
  videoSrc?: string;
  imageSrc?: string;
  title: string;
  description: string;
}

export function MediaPreviewCard({
  audioSrc,
  videoSrc,
  imageSrc,
  title,
  description,
}: MediaPreviewCardProps) {
  const [isVideoVisible, setIsVideoVisible] = useState(false);

  // Smart resolver for image sources
  const resolveImageSrc = (src?: string) => {
    if (!src) return "https://placehold.co/640x360?text=No+Thumbnail";
    if (src.startsWith("blob:")) return src;
    if (src.startsWith("data:")) return src;
    if (src.startsWith("http")) return src;
    return src;
  };

  const hasVideo = !!videoSrc;
  const hasAudio = !!audioSrc;
  const hasImage = !!imageSrc;

  const handleToggleVideo = () => {
    if (hasVideo && hasImage) setIsVideoVisible((prev) => !prev);
  };

  const resolvedImage = resolveImageSrc(imageSrc);

  // ---- YouTube length checks ----
  const titleTooLong = title && title.length > 100;
  const descTooLong = description && description.length > 5000;

  return (
    <div className="flex flex-col gap-4 w-full sticky top-0">
      <div className="w-full aspect-video rounded border border-muted overflow-hidden relative bg-muted/20">
        {/* Video with thumbnail toggle */}
        {hasVideo && hasImage && !isVideoVisible ? (
          <div
            className="relative w-full h-full cursor-pointer"
            onClick={handleToggleVideo}
          >
            <img
              src={resolvedImage}
              alt="Custom thumbnail"
              className="absolute inset-0 w-full h-full object-cover"
              crossOrigin="anonymous"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src =
                  "https://placehold.co/640x360?text=Failed+to+Load";
              }}
            />
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-lg">
              Click to play video
            </div>
          </div>
      ) : hasVideo ? (
  <video
    src={videoSrc}
    controls
    autoPlay={isVideoVisible}
    className="w-full h-full object-cover"
  />
        ) : hasAudio && hasImage ? (
          <>
            <img
              src={resolvedImage}
              alt="Audio thumbnail"
              className="absolute inset-0 w-full h-full object-cover z-0"
              crossOrigin="anonymous"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src =
                  "https://placehold.co/640x360?text=Failed+to+Load";
              }}
            />
            <div className="absolute bottom-0 left-0 right-0 z-10 p-4 bg-black/60">
              <audio src={audioSrc} controls className="w-full" />
            </div>
          </>
        ) : hasAudio ? (
          <div className="p-4">
            <audio src={audioSrc} controls className="w-full" />
          </div>
        ) : hasImage ? (
          <img
            src={resolvedImage}
            alt="Thumbnail"
            className="w-full h-full object-cover"
            crossOrigin="anonymous"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src =
                "https://placehold.co/640x360?text=Failed+to+Load";
            }}
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full text-muted-foreground">
            No media selected
          </div>
        )}
      </div>

      <div className="text-sm space-y-1">
        <h3 className="text-lg font-semibold">
          {title || (
            <span className="text-muted-foreground italic">
              Your title will appear here
            </span>
          )}
        </h3>
        {titleTooLong && (
          <p className="text-xs text-red-500">
            Title is too long ({title.length}/100 characters)
          </p>
        )}

   <div className="whitespace-pre-wrap text-muted-foreground break-words">
  {description ? (
    <>
      {renderLinkifiedTextYouTubeStrict(description)}
      {descTooLong && (
        <p className="text-xs text-red-500 mt-1">
          Description is too long ({description.length}/5000 characters)
        </p>
      )}
    </>
  ) : (
    <span className="italic">
      Add a description to help listeners understand your track.
    </span>
  )}
</div>
      </div>
    </div>
  );
}
